import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, deleteField,
  writeBatch, query, orderBy, limit, startAfter, where, onSnapshot,
  getCountFromServer, type DocumentSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase';
import { COLLECTIONS, STORAGE_PATHS, DAILY_VERSE_SETTINGS_DOC, DEFAULT_DAILY_VERSE_TIME, REVIEW_PAGES_PATH, STORIES_DOC, MEANINGS_VERSIONS_DOC, OVI_TEXT_VERSIONS_DOC, validateStoriesFile } from '@dnyaneshwari/shared';
import type { Chapter, ChapterMedia, ChapterPart, Ovi, AppUser, DailyVerse, DailyVerseSettings, TextCorrection, ReviewProgress, ChapterMeanings, StoriesFile } from '@dnyaneshwari/shared';
import { auth } from './firebase';

export interface RawOvi {
  oviNumber: number;
  textMarathi: string;
  arthMarathi: string;
  textEnglish?: string;
}

// ─── Chapters ──────────────────────────────────────────────────────────────

export async function getChapters(): Promise<Chapter[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.CHAPTERS), orderBy('sortOrder'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Chapter));
}

export async function upsertChapter(data: Partial<Chapter> & { id: string }): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.CHAPTERS, data.id), data, { merge: true });
}

// ─── Ovis ──────────────────────────────────────────────────────────────────

export async function getOvis(
  chapterId: string,
  pageSize = 50,
  after?: DocumentSnapshot
): Promise<{ ovis: Ovi[]; last: DocumentSnapshot | null }> {
  const constraints = [
    where('chapterId', '==', chapterId),
    orderBy('oviNumber'),
    limit(pageSize),
    ...(after ? [startAfter(after)] : []),
  ] as const;
  const snap = await getDocs(query(collection(db, COLLECTIONS.OVIS), ...constraints));
  return {
    ovis: snap.docs.map((d) => ({ id: d.id, ...d.data() } as Ovi)),
    last: snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null,
  };
}

export async function getOviCount(chapterId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(db, COLLECTIONS.OVIS), where('chapterId', '==', chapterId))
  );
  return snap.data().count;
}

export async function importOvis(
  chapterId: string,
  rawOvis: RawOvi[],
  onProgress: (done: number, total: number) => void
): Promise<void> {
  const BATCH_SIZE = 400;
  const paddedChapter = chapterId.padStart(2, '0');
  const chapterNum = parseInt(paddedChapter, 10);

  for (let i = 0; i < rawOvis.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = rawOvis.slice(i, i + BATCH_SIZE);

    for (const raw of chunk) {
      const paddedOvi = String(raw.oviNumber).padStart(3, '0');
      const oviId = `${paddedChapter}-${paddedOvi}`;
      const oviDoc: Record<string, unknown> = {
        chapterId: paddedChapter,
        chapterNumber: chapterNum,
        oviNumber: raw.oviNumber,
        textMarathi: raw.textMarathi,
        arthMarathi: raw.arthMarathi,
        audioSegmentIndex: 0,
        searchText: `${raw.textMarathi} ${raw.arthMarathi}`.toLowerCase(),
        sortOrder: raw.oviNumber,
      };
      if (raw.textEnglish) oviDoc.textEnglish = raw.textEnglish;
      batch.set(doc(db, COLLECTIONS.OVIS, oviId), oviDoc);
    }

    await batch.commit();
    onProgress(Math.min(i + BATCH_SIZE, rawOvis.length), rawOvis.length);
  }

  await updateDoc(doc(db, COLLECTIONS.CHAPTERS, paddedChapter), {
    totalOvis: rawOvis.length,
  });
}

export async function getOviById(oviId: string): Promise<Ovi | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.OVIS, oviId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Ovi) : null;
}

export function parseOvisJson(raw: string): RawOvi[] {
  const parsed = JSON.parse(raw);

  // Format A: plain array
  if (Array.isArray(parsed)) {
    return parsed.map((o, idx) => ({
      oviNumber: o.oviNumber ?? o.number ?? idx + 1,
      textMarathi: o.textMarathi ?? o.text ?? '',
      arthMarathi: o.arthMarathi ?? o.arth ?? '',
      textEnglish: o.textEnglish,
    }));
  }

  // Format B: { chapter, ovis: [...] }
  if (parsed.ovis && Array.isArray(parsed.ovis)) {
    return parsed.ovis.map((o: Record<string, unknown>, idx: number) => ({
      oviNumber: (o.oviNumber ?? o.number ?? idx + 1) as number,
      textMarathi: (o.textMarathi ?? o.text ?? '') as string,
      arthMarathi: (o.arthMarathi ?? o.arth ?? '') as string,
      textEnglish: o.textEnglish as string | undefined,
    }));
  }

  throw new Error('Unrecognized JSON format. Expected array or { ovis: [...] }.');
}

// ─── Users ─────────────────────────────────────────────────────────────────

export async function getUsers(
  pageSize = 20,
  after?: DocumentSnapshot
): Promise<{ users: AppUser[]; last: DocumentSnapshot | null }> {
  const constraints = [
    orderBy('lastActiveAt', 'desc'),
    limit(pageSize),
    ...(after ? [startAfter(after)] : []),
  ] as const;
  const snap = await getDocs(query(collection(db, COLLECTIONS.USERS), ...constraints));
  return {
    users: snap.docs.map((d) => ({ id: d.id, ...d.data() } as AppUser)),
    last: snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null,
  };
}

export async function getTotalUserCount(): Promise<number> {
  const snap = await getCountFromServer(collection(db, COLLECTIONS.USERS));
  return snap.data().count;
}

export async function getTotalOviCount(): Promise<number> {
  const snap = await getCountFromServer(collection(db, COLLECTIONS.OVIS));
  return snap.data().count;
}

export async function getTotalChapterCount(): Promise<number> {
  const snap = await getCountFromServer(collection(db, COLLECTIONS.CHAPTERS));
  return snap.data().count;
}

// ─── Daily Verse ────────────────────────────────────────────────────────────

/**
 * Schedules the verse of the day. The text travels with the schedule doc
 * because the scripture is a static bundle, not Firestore: the 5:30 push
 * needs a body without a lookup, and the app resolves the ovi by id itself.
 */
export async function setDailyVerse(
  date: string,
  oviId: string,
  chapterId: string,
  extra: { oviNumber?: number; textMarathi?: string; sendTime?: string } = {}
): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.DAILY_VERSES, date), {
    oviId,
    chapterId,
    ...extra,
    scheduledAt: new Date(date),
    pushSent: false,
  });
}

export async function removeDailyVerse(date: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.DAILY_VERSES, date));
}

/** Global daily-verse send time (HH:mm IST); the default until an admin sets one. */
export async function getDailyVerseSettings(): Promise<DailyVerseSettings> {
  const snap = await getDoc(doc(db, COLLECTIONS.SETTINGS, DAILY_VERSE_SETTINGS_DOC));
  const data = snap.exists() ? (snap.data() as Partial<DailyVerseSettings>) : {};
  return { sendTime: data.sendTime || DEFAULT_DAILY_VERSE_TIME };
}

export async function setDailyVerseSettings(settings: DailyVerseSettings): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.SETTINGS, DAILY_VERSE_SETTINGS_DOC), settings, { merge: true });
}

export async function getMonthVerses(year: number, month: number): Promise<DailyVerse[]> {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0, 23, 59, 59);
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.DAILY_VERSES),
      where('scheduledAt', '>=', start),
      where('scheduledAt', '<=', end)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as DailyVerse));
}

// ─── Audio ──────────────────────────────────────────────────────────────────

export async function uploadAudio(
  chapterId: string,
  file: File,
  oviFrom: number,
  oviTo: number,
  onProgress: (pct: number) => void
): Promise<string> {
  const paddedChapter = chapterId.padStart(2, '0');
  const filename = `${paddedChapter}_${oviFrom}-${oviTo}.mp3`;
  const storageRef = ref(storage, `${STORAGE_PATHS.AUDIO}/${filename}`);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

// ─── Video ──────────────────────────────────────────────────────────────────

/**
 * Uploads a chapter video to Storage. Nothing is written to Firestore here —
 * the extractChapterAudio function picks the object up, pulls out the audio
 * track, and writes both URLs onto the chapter doc itself.
 */
export function uploadVideo(
  chapterId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<void> {
  const paddedChapter = chapterId.padStart(2, '0');
  const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
  const storageRef = ref(storage, `${STORAGE_PATHS.VIDEO}/${paddedChapter}.${ext}`);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'video/mp4',
    });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => resolve()
    );
  });
}

/** Live-watches a chapter's media field. Returns an unsubscribe function. */
export function watchChapterMedia(
  chapterId: string,
  onMedia: (media: ChapterMedia | null) => void
): () => void {
  const paddedChapter = chapterId.padStart(2, '0');
  return onSnapshot(doc(db, COLLECTIONS.CHAPTERS, paddedChapter), (snap) => {
    onMedia((snap.data() as Chapter | undefined)?.media ?? null);
  });
}

export async function addAudioSegment(
  chapterId: string,
  segment: { audioUrl: string; oviFrom: number; oviTo: number; durationMs: number }
): Promise<void> {
  const paddedChapter = chapterId.padStart(2, '0');
  const chapterRef = doc(db, COLLECTIONS.CHAPTERS, paddedChapter);
  const snap = await getDoc(chapterRef);
  if (!snap.exists()) return;

  const chapter = snap.data() as Chapter;
  const audioSegments = [...(chapter.audioSegments || []), segment];
  const segmentIndex = audioSegments.length - 1;

  const ovisSnap = await getDocs(
    query(
      collection(db, COLLECTIONS.OVIS),
      where('chapterId', '==', paddedChapter),
      where('oviNumber', '>=', segment.oviFrom),
      where('oviNumber', '<=', segment.oviTo)
    )
  );

  const batch = writeBatch(db);
  ovisSnap.docs.forEach((d) => batch.update(d.ref, { audioSegmentIndex: segmentIndex }));
  batch.update(chapterRef, { audioSegments });
  await batch.commit();
}

// ─── Notifications ─────────────────────────────────────────────────────────

export interface SentNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  sentAt: unknown;
  sentBy: string;
  recipientCount: number;
  openedCount: number;
}

/**
 * Sends a push notification via the sendPushNotification callable.
 *
 * The callable re-checks that the caller is an admin server-side, so this is
 * not the security boundary -- it just surfaces the result to the UI.
 */
export async function sendPushNotification(input: {
  title: string;
  body: string;
  topic: string;
  imageUrl?: string;
  targetOviId?: string;
}): Promise<void> {
  const callable = httpsCallable<typeof input, { success: boolean }>(
    functions,
    'sendPushNotification'
  );
  await callable(input);
}

/** Most recently sent notifications, newest first. */
export async function getRecentNotifications(max = 10): Promise<SentNotification[]> {
  const snap = await getDocs(
    query(collection(db, 'notifications'), orderBy('sentAt', 'desc'), limit(max))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as SentNotification));
}

/**
 * Removes a notification from the history.
 *
 * This deletes the record, not the push -- anything already delivered stays on
 * the recipient's device. What it does change is the in-app notification list,
 * which reads this same collection, so the entry disappears for users too.
 */
export async function deleteNotification(id: string): Promise<void> {
  await deleteDoc(doc(db, 'notifications', id));
}

// ─── Chapter parts ─────────────────────────────────────────────────────────
//
// A chapter is split into parts because the recordings arrive as several files
// -- chapter 1 is three. The part, not the chapter, owns a video, its extracted
// audio, and the ovi range it covers. Storage keys are part-scoped
// (video/01/02.mp4) so uploading a second file can no longer overwrite the
// first, which the old chapter-scoped path (video/01.mp4) did silently.

function partsRef(chapterId: string) {
  const padded = chapterId.padStart(2, '0');
  return collection(db, COLLECTIONS.CHAPTERS, padded, COLLECTIONS.PARTS);
}

function partRef(chapterId: string, partId: string) {
  const padded = chapterId.padStart(2, '0');
  return doc(db, COLLECTIONS.CHAPTERS, padded, COLLECTIONS.PARTS, partId);
}

export function padPartId(index: number): string {
  return String(index).padStart(2, '0');
}

export async function getParts(chapterId: string): Promise<ChapterPart[]> {
  const snap = await getDocs(query(partsRef(chapterId), orderBy('index')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ChapterPart);
}

/**
 * Live-watches a chapter's parts so upload progress and extraction status
 * stream in. onError matters: a denied listener otherwise fails silently and
 * leaves the panel loading forever with nothing to explain why.
 */
export function watchParts(
  chapterId: string,
  onParts: (parts: ChapterPart[]) => void,
  onError?: (message: string) => void
): () => void {
  return onSnapshot(
    query(partsRef(chapterId), orderBy('index')),
    (snap) => onParts(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ChapterPart)),
    (err) => onError?.(err.message)
  );
}

/** Appends an empty part. Files and ovi range are filled in afterwards. */
export async function createPart(
  chapterId: string,
  defaults: Partial<ChapterPart> = {}
): Promise<ChapterPart> {
  const existing = await getParts(chapterId);
  const index = existing.length ? Math.max(...existing.map((p) => p.index)) + 1 : 1;
  const id = padPartId(index);
  const part: ChapterPart = {
    id,
    index,
    titleMarathi: `भाग ${index}`,
    titleEnglish: `Part ${index}`,
    oviFrom: 0,
    oviTo: 0,
    durationMs: 0,
    status: 'empty',
    ...defaults,
  };
  const { id: _omit, ...data } = part;
  await setDoc(partRef(chapterId, id), data);
  await refreshChapterRollups(chapterId);
  return part;
}

export async function updatePart(
  chapterId: string,
  partId: string,
  patch: Partial<ChapterPart>
): Promise<void> {
  const { id: _omit, ...data } = patch as Partial<ChapterPart> & { id?: string };
  await updateDoc(partRef(chapterId, partId), { ...data, updatedAt: new Date() });
  await refreshChapterRollups(chapterId);
}

export async function deletePart(chapterId: string, partId: string): Promise<void> {
  await deleteDoc(partRef(chapterId, partId));
  await refreshChapterRollups(chapterId);
}

/**
 * Rewrites every part's index to match the given order. Document ids stay put
 * so the storage objects keep pointing at the right part -- only `index`, which
 * drives playback order, moves.
 */
export async function reorderParts(chapterId: string, orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((partId, i) => {
    batch.update(partRef(chapterId, partId), { index: i + 1 });
  });
  await batch.commit();
  await refreshChapterRollups(chapterId);
}

/**
 * Denormalises part count and total duration onto the chapter doc. The mobile
 * list screens read every chapter in one query, so they must never have to open
 * 18 subcollections to render a duration.
 */
export async function refreshChapterRollups(chapterId: string): Promise<void> {
  const parts = await getParts(chapterId);
  const ready = parts.filter((p) => p.status === 'ready').sort((a, b) => a.index - b.index);
  const padded = chapterId.padStart(2, '0');
  // Mirror the first ready part onto the chapter's media field: that is what
  // lists a chapter under Listen and Videos in the app. The video pipeline
  // writes it too; doing it here as well means an audio-only chapter (no
  // video uploaded at all) shows up just the same.
  const first = ready[0];
  const mirror = first
    ? {
        media: {
          videoUrl: first.videoUrl ?? '',
          audioUrl: first.audioUrl ?? '',
          durationMs: first.durationMs || 0,
          sourceVideoPath: first.sourceVideoPath ?? '',
          sourceGeneration: first.sourceGeneration ?? '',
          cdn: first.cdn ?? 'firebase',
          extractedAt: new Date(),
        },
      }
    // No ready part left (all deleted, or a fresh start): clear the mirror
    // too, or the app would keep listing recordings that no longer exist.
    : { media: deleteField() };
  await setDoc(
    doc(db, COLLECTIONS.CHAPTERS, padded),
    {
      partCount: parts.length,
      totalDurationMs: ready.reduce((sum, p) => sum + (p.durationMs || 0), 0),
      ...mirror,
    },
    { merge: true }
  );
}

/**
 * Uploads a part's video. The extractChapterAudio function picks the object up,
 * pulls out the audio track and writes both URLs plus the duration back onto
 * this part doc, flipping status to ready.
 */
export function uploadPartVideo(
  chapterId: string,
  partId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<void> {
  const padded = chapterId.padStart(2, '0');
  const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
  const storageRef = ref(storage, `${STORAGE_PATHS.VIDEO}/${padded}/${partId}.${ext}`);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'video/mp4',
    });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => {
        await updateDoc(partRef(chapterId, partId), {
          status: 'processing',
          videoFileName: file.name,
        });
        resolve();
      }
    );
  });
}

/**
 * Uploads a part's master recording.
 *
 * It goes to the private `audio-src` folder. The publishPartAudio function
 * picks it up, encodes the 96 kbps streaming copy, puts that on Cloudflare
 * and sets the part's audioUrl and duration, flipping it to ready -- about a
 * minute. Until then the part shows "Processing" and keeps playing whatever
 * it had before.
 */
export function uploadPartAudio(
  chapterId: string,
  partId: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<void> {
  const padded = chapterId.padStart(2, '0');
  const ext = (file.name.split('.').pop() || 'mp3').toLowerCase();
  const storageRef = ref(storage, `${STORAGE_PATHS.AUDIO_SOURCE}/${padded}/${partId}.${ext}`);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'audio/mpeg',
    });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => {
        await updateDoc(partRef(chapterId, partId), {
          audioFileName: file.name,
          audioSource: 'uploaded',
          status: 'processing',
          updatedAt: new Date(),
        });
        resolve();
      }
    );
  });
}

/**
 * Moves a chapter's single legacy `media` object into a first part, so chapter
 * 1's already-uploaded recording survives the model change. Safe to call twice:
 * it does nothing once the chapter has parts.
 */
export async function migrateLegacyMedia(chapterId: string): Promise<boolean> {
  const existing = await getParts(chapterId);
  if (existing.length > 0) return false;

  const padded = chapterId.padStart(2, '0');
  const snap = await getDoc(doc(db, COLLECTIONS.CHAPTERS, padded));
  const media = (snap.data() as Chapter | undefined)?.media;
  if (!media?.videoUrl && !media?.audioUrl) return false;

  await createPart(chapterId, {
    videoUrl: media.videoUrl,
    audioUrl: media.audioUrl,
    durationMs: media.durationMs ?? 0,
    sourceVideoPath: media.sourceVideoPath,
    sourceGeneration: media.sourceGeneration,
    status: 'ready',
  });
  return true;
}

/**
 * Throws away a chapter's legacy single recording -- the Firestore pointer and
 * the objects behind it -- so a chapter can be rebuilt from parts. Used to drop
 * placeholder content that was uploaded before the real recordings arrived.
 * Storage deletes are best-effort: a missing object is not a failure.
 */
export async function discardLegacyMedia(chapterId: string): Promise<void> {
  const padded = chapterId.padStart(2, '0');
  const chapterRef = doc(db, COLLECTIONS.CHAPTERS, padded);
  const snap = await getDoc(chapterRef);
  const media = (snap.data() as Chapter | undefined)?.media;

  const objectPaths = [
    media?.sourceVideoPath,
    `${STORAGE_PATHS.AUDIO}/${padded}.m4a`,
  ].filter((p): p is string => Boolean(p));

  await Promise.allSettled(objectPaths.map((path) => deleteObject(ref(storage, path))));
  await updateDoc(chapterRef, { media: deleteField() });
}

// ============ Text review (proofreading against the book) ============

function pageKey(n: number): string {
  return `${REVIEW_PAGES_PATH}/${String(n).padStart(3, '0')}.jpg`;
}

/** Download URL for a rendered book page; null if that page was never uploaded. */
export async function getReviewPageUrl(page: number): Promise<string | null> {
  try {
    return await getDownloadURL(ref(storage, pageKey(page)));
  } catch {
    return null;
  }
}

/** Whether the book pages have been uploaded at all (checks page 9, chapter 1). */
export async function reviewPagesPresent(): Promise<boolean> {
  try {
    await getMetadata(ref(storage, pageKey(9)));
    return true;
  } catch {
    return false;
  }
}

/**
 * Uploads rendered pages named p-NNN.jpg (pdftoppm's naming) or NNN.jpg.
 * Files that do not look like a page are skipped. Runs a few at a time so a
 * 500-page folder finishes in a minute or two without hammering the browser.
 */
export async function uploadReviewPages(
  files: File[],
  onProgress: (done: number, total: number) => void
): Promise<number> {
  const pages = files
    .map((f) => ({ f, m: /(\d{1,3})\.jpe?g$/i.exec(f.name) }))
    .filter((x): x is { f: File; m: RegExpExecArray } => !!x.m)
    .map((x) => ({ f: x.f, n: parseInt(x.m[1], 10) }));
  let done = 0;
  const queue = [...pages];
  const worker = async () => {
    for (let job = queue.shift(); job; job = queue.shift()) {
      await uploadBytes(ref(storage, pageKey(job.n)), job.f, { contentType: 'image/jpeg' });
      done += 1;
      onProgress(done, pages.length);
    }
  };
  await Promise.all(Array.from({ length: 4 }, worker));
  return pages.length;
}

export async function getCorrections(chapterId: string): Promise<TextCorrection[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.TEXT_CORRECTIONS), where('chapterId', '==', chapterId))
  );
  return snap.docs.map((d) => {
    const data = d.data();
    return { id: d.id, ...data, updatedAt: data.updatedAt?.toDate?.() ?? new Date() } as TextCorrection;
  });
}

/** Every correction, for export to scripts/corrections.json. */
export async function getAllCorrections(): Promise<TextCorrection[]> {
  const snap = await getDocs(collection(db, COLLECTIONS.TEXT_CORRECTIONS));
  return snap.docs.map((d) => {
    const data = d.data();
    return { id: d.id, ...data, updatedAt: data.updatedAt?.toDate?.() ?? new Date() } as TextCorrection;
  });
}

export async function saveCorrection(
  c: Omit<TextCorrection, 'updatedAt' | 'updatedBy'>
): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.TEXT_CORRECTIONS, c.id), {
    ...c,
    updatedAt: new Date(),
    updatedBy: auth.currentUser?.email ?? auth.currentUser?.uid ?? 'admin',
  });
}

export async function removeCorrection(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.TEXT_CORRECTIONS, id));
}

export async function getReviewProgress(chapterId: string): Promise<ReviewProgress | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.REVIEW_PROGRESS, chapterId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return { chapterId, ...data, updatedAt: data.updatedAt?.toDate?.() ?? new Date() } as ReviewProgress;
}

export async function getAllReviewProgress(): Promise<ReviewProgress[]> {
  const snap = await getDocs(collection(db, COLLECTIONS.REVIEW_PROGRESS));
  return snap.docs.map((d) => {
    const data = d.data();
    return { chapterId: d.id, ...data, updatedAt: data.updatedAt?.toDate?.() ?? new Date() } as ReviewProgress;
  });
}

export async function setReviewProgress(
  chapterId: string,
  patch: Partial<Pick<ReviewProgress, 'page' | 'verifiedUpTo'>>
): Promise<void> {
  await setDoc(
    doc(db, COLLECTIONS.REVIEW_PROGRESS, chapterId),
    { ...patch, updatedAt: new Date() },
    { merge: true }
  );
}

// ============ भावार्थ (per-ovi explanations, Firestore meanings/{chapterId}) ============

export async function getChapterMeanings(chapterId: string): Promise<Record<string, string>> {
  const snap = await getDoc(doc(db, COLLECTIONS.MEANINGS, chapterId.padStart(2, '0')));
  const data = snap.exists() ? (snap.data() as Partial<ChapterMeanings>) : {};
  return (data.items as Record<string, string> | undefined) ?? {};
}

/** Merges the given items into the chapter's doc; other ovis keep theirs. */
export async function saveChapterMeanings(chapterId: string, items: Record<string, string>): Promise<void> {
  const id = chapterId.padStart(2, '0');
  const fields: Record<string, unknown> = { chapterId: id, updatedAt: new Date() };
  for (const [k, v] of Object.entries(items)) fields[`items.${k}`] = v;
  const ref = doc(db, COLLECTIONS.MEANINGS, id);
  // updateDoc needs the doc to exist; setDoc with merge handles both cases
  // but cannot address nested keys, so create-then-update.
  const snap = await getDoc(ref);
  if (!snap.exists()) await setDoc(ref, { chapterId: id, items: {}, updatedAt: new Date() });
  await updateDoc(ref, fields);
  // Tell installed apps this chapter changed, so they re-download it once
  // instead of fetching every chapter on every open.
  await setDoc(doc(db, COLLECTIONS.CONTENT, MEANINGS_VERSIONS_DOC), { versions: { [id]: Date.now() } }, { merge: true });
}

// ============ ओव्या (corrected verses, Firestore oviText/{chapterId}) ============

export async function getChapterOviText(chapterId: string): Promise<Record<string, string>> {
  const snap = await getDoc(doc(db, COLLECTIONS.OVI_TEXT, chapterId.padStart(2, '0')));
  const items = snap.exists() ? (snap.data() as { items?: Record<string, string> }).items : undefined;
  return items ?? {};
}

/**
 * Merges corrected verses into the chapter's document; ovis not named here
 * keep whatever they had, and any ovi with no correction keeps the text the
 * app shipped with.
 */
export async function saveChapterOviText(chapterId: string, items: Record<string, string>): Promise<void> {
  const id = chapterId.padStart(2, '0');
  const fields: Record<string, unknown> = { chapterId: id, updatedAt: new Date() };
  for (const [k, v] of Object.entries(items)) fields[`items.${k}`] = v;
  const ref = doc(db, COLLECTIONS.OVI_TEXT, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) await setDoc(ref, { chapterId: id, items: {}, updatedAt: new Date() });
  await updateDoc(ref, fields);
  // Tells installed apps this chapter changed, so they fetch it once rather
  // than checking every chapter on every open.
  await setDoc(doc(db, COLLECTIONS.CONTENT, OVI_TEXT_VERSIONS_DOC), { versions: { [id]: Date.now() } }, { merge: true });
}

/** Removes a correction, so the app falls back to the text it shipped with. */
export async function clearOviTextCorrection(chapterId: string, oviNumber: number): Promise<void> {
  const id = chapterId.padStart(2, '0');
  const ref = doc(db, COLLECTIONS.OVI_TEXT, id);
  await updateDoc(ref, { [`items.${String(oviNumber).padStart(3, '0')}`]: deleteField(), updatedAt: new Date() });
  await setDoc(doc(db, COLLECTIONS.CONTENT, OVI_TEXT_VERSIONS_DOC), { versions: { [id]: Date.now() } }, { merge: true });
}

const DEV_DIGITS = '०१२३४५६७८९';
const latinDigits = (s: string) => s.replace(/[०-९]/g, (d) => String(DEV_DIGITS.indexOf(d)));

/**
 * Parses a chapter's explanations from free text. Each explanation begins
 * with its ovi number (either script) followed by ".", ":", ")" or a space,
 * and runs until the next such line. Returns items keyed by padded number.
 */
export function parseMeaningsText(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  let current: string | null = null;
  const buf: string[] = [];
  const flush = () => {
    if (current !== null) {
      const body = buf.join('\n').trim();
      if (body) out[current] = body;
    }
    buf.length = 0;
  };
  for (const raw of text.replace(/\r/g, '').split('\n')) {
    const m = /^\s*(?:ओवी\s*)?([०-९0-9]{1,4})\s*[.:)\-–]?\s+(.*)$/.exec(raw);
    if (m) {
      flush();
      current = String(parseInt(latinDigits(m[1]), 10)).padStart(3, '0');
      buf.push(m[2]);
    } else if (current !== null) {
      buf.push(raw);
    }
  }
  flush();
  return out;
}

/**
 * Reruns audio extraction / publishing for a part whose upload the storage
 * trigger failed on (it sat on "processing"). Takes a few minutes for a
 * large video; the part's status updates live through watchParts.
 */
/** Runs the audio publish again for a part stuck on processing or failed. */
export async function republishAudio(chapterId: string, partId: string): Promise<void> {
  const fn = httpsCallable<{ chapterId: string; partId: string }, { ok: boolean }>(functions, 'republishPartAudio');
  await fn({ chapterId: chapterId.padStart(2, '0'), partId });
}

export async function reprocessPart(chapterId: string, partId: string): Promise<void> {
  const fn = httpsCallable<{ chapterId: string; partId: string }, { ok: boolean }>(functions, 'reprocessPartMedia');
  await fn({ chapterId: chapterId.padStart(2, '0'), partId });
}

/**
 * Marks a part ready on its uploaded audio alone, abandoning the video the
 * pipeline is stuck on. The app plays the audio; the video was only ever
 * needed for timing, which happens off the app's storage now.
 */
export async function keepAudioOnly(chapterId: string, partId: string): Promise<void> {
  await updateDoc(partRef(chapterId, partId), {
    status: 'ready',
    videoUrl: deleteField(),
    sourceVideoPath: deleteField(),
    sourceGeneration: deleteField(),
    errorMessage: deleteField(),
    updatedAt: new Date(),
  });
  await refreshChapterRollups(chapterId);
}

// ─── माऊलींविषयी: stories and quiz (content/stories) ───────────────────────

/** The published document, or null when nothing has been published yet. */
export async function getPublishedStories(): Promise<StoriesFile | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.CONTENT, STORIES_DOC));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<StoriesFile>;
  return Array.isArray(data.stories) ? (data as StoriesFile) : null;
}

/**
 * Publishes the whole file as one document. The version is today's date
 * with a counter, so the app can tell a newer copy from the bundled one.
 */
export async function publishStories(file: StoriesFile): Promise<string> {
  const problems = validateStoriesFile(file);
  if (problems.length) throw new Error(problems.join('\n'));
  const today = new Date().toISOString().slice(0, 10);
  const [prevDate, prevN] = (file.version || '').split('.');
  const n = prevDate === today ? Number(prevN || 0) + 1 : 1;
  const version = `${today}.${n}`;
  const out: StoriesFile = { ...file, version, generatedAt: new Date().toISOString() };
  await setDoc(doc(db, COLLECTIONS.CONTENT, STORIES_DOC), {
    ...out,
    publishedBy: auth.currentUser?.email ?? auth.currentUser?.uid ?? '',
    publishedAt: new Date(),
  });
  return version;
}
